import type { ProjectVersionOverview } from '@/types/project';
import type { ThreatCatNote, NoteType } from '@/types/note';
import type { EntityType } from '@/types/diagram';
import { throwApiError } from '@/lib/errors';

const BACKEND_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';

export interface ParsedPath {
  name: string;
  description: string;
  authentication: string;
  protocol: string;
  source_id: number | null;
  target_id: number | null;
  position: unknown;
  style: unknown;
  id: number;
  uuid: string;
  diagram_id: number;
}

export interface ParsedEntity {
  name: string;
  id: number;
  type: string;
  diagram_id: number;
}

export interface ParsedDiagram {
  name: string;
  description: string;
  entities: ParsedEntity[];
  boundaries: unknown[];
  paths: ParsedPath[];
  unresolved_paths: ParsedPath[];
  diagram_schema_version: number;
}

export interface ImportedDiagramResult {
  name: string;
  ok: boolean;
  error?: string;
}

export const threatcatService = {
  fetchThreatCatProjects: async (cookie: string): Promise<ProjectVersionOverview[]> => {
    const response = await fetch(`${BACKEND_BASE}/api/threatcat/projects`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'X-ThreatCat-Cookie': cookie },
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch ThreatCat projects');
    }
    const data = await response.json();
    if (!Array.isArray(data)) {
      throw new Error('Invalid response from ThreatCat: expected project list');
    }
    return data;
  },

  fetchThreatCatProjectExport: async (cookie: string, projectVersionId: number, projectId?: number): Promise<unknown> => {
    const params = projectId != null ? `?project_id=${projectId}` : '';
    const response = await fetch(`${BACKEND_BASE}/api/threatcat/project-export/${projectVersionId}${params}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'X-ThreatCat-Cookie': cookie },
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch project export from ThreatCat');
    }
    return response.json();
  },

  fetchThreatCatNotes: async (cookie: string, projectVersionId: number): Promise<ThreatCatNote[]> => {
    const response = await fetch(`${BACKEND_BASE}/api/threatcat/notes/${projectVersionId}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'X-ThreatCat-Cookie': cookie },
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch notes from ThreatCat');
    }
    return response.json();
  },

  createThreatCatNote: async (cookie: string, projectVersionId: number, type: NoteType, value: string): Promise<ThreatCatNote> => {
    const response = await fetch(`${BACKEND_BASE}/api/threatcat/notes/${projectVersionId}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-ThreatCat-Cookie': cookie },
      body: JSON.stringify({ type, value }),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to create note in ThreatCat');
    }
    return response.json();
  },

  updateThreatCatNote: async (cookie: string, projectVersionId: number, noteId: number, type: NoteType, value: string): Promise<ThreatCatNote> => {
    const response = await fetch(`${BACKEND_BASE}/api/threatcat/notes/${projectVersionId}/${noteId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-ThreatCat-Cookie': cookie },
      body: JSON.stringify({ type, value }),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update note in ThreatCat');
    }
    return response.json();
  },

  deleteThreatCatNote: async (cookie: string, projectVersionId: number, noteId: number): Promise<void> => {
    const response = await fetch(`${BACKEND_BASE}/api/threatcat/notes/${projectVersionId}/${noteId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'X-ThreatCat-Cookie': cookie },
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to delete note in ThreatCat');
    }
  },

  pushToThreatCat: async (payload: {
    project_version_id: string;
    cookie: string;
    vulnerability_id: number;
  }): Promise<{ ok: boolean; threat?: Record<string, unknown> }> => {
    const response = await fetch(`${BACKEND_BASE}/api/vulnerabilities/${payload.vulnerability_id}/push`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-ThreatCat-Cookie': payload.cookie,
        'X-ThreatCat-Project-Version-Id': payload.project_version_id,
      },
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to push to ThreatCat');
    }
    return response.json();
  },
  updatePath: async (payload: {
    cookie: string;
    id: number;
    uuid?: string;
    name: string;
    description?: string;
    authentication: string;
    protocol: string;
    source_id: number;
    target_id: number;
    diagram_id: number;
    position?: Record<string, unknown> | null;
    style?: Record<string, unknown> | null;
  }): Promise<{ ok: boolean }> => {
    const { cookie, ...body } = payload;
    const response = await fetch(`${BACKEND_BASE}/api/update-path`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-ThreatCat-Cookie': cookie,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update path in ThreatCat');
    }
    return response.json();
  },
  updateEntity: async (payload: {
    cookie: string;
    id: number;
    diagram_id: number;
    name: string;
    description: string;
    label: string;
    type: EntityType;
    boundary_id: number | null;
  }): Promise<{ ok: boolean }> => {
    const { cookie, ...body } = payload;
    const response = await fetch(`${BACKEND_BASE}/api/update-entity`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-ThreatCat-Cookie': cookie,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update entity in ThreatCat');
    }
    return response.json();
  },

  updateBoundary: async (payload: {
    cookie: string;
    id: number;
    diagram_id: number;
    name: string;
    description: string;
    parent_boundary_id: number | null;
  }): Promise<{ ok: boolean }> => {
    const { cookie, ...body } = payload;
    const response = await fetch(`${BACKEND_BASE}/api/update-boundary`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-ThreatCat-Cookie': cookie,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update boundary in ThreatCat');
    }
    return response.json();
  },

  parseTm7: async (file: File): Promise<{ diagrams: ParsedDiagram[] }> => {
    const form = new FormData();
    form.append('file', file);

    const response = await fetch(`${BACKEND_BASE}/api/threatcat/parse-tm7`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to parse TM7 file');
    }
    return response.json();
  },

  createThreat: async (payload: {
    project_version_id: string;
    cookie: string;
    name: string;
    scenario: string;
    vulnerability: string;
    countermeasure: string;
    rating_vector_string: string | null;
    status: string;
  }): Promise<{ ok: boolean; threat: import('@/types/threat').Threat }> => {
    const { cookie, ...body } = payload;
    const response = await fetch(`${BACKEND_BASE}/api/create-threat`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-ThreatCat-Cookie': cookie },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to create threat in ThreatCat');
    }
    return response.json();
  },

  updateThreat: async (payload: {
    project_version_id: string;
    threat_id: number;
    cookie: string;
    name: string;
    scenario: string;
    vulnerability: string;
    countermeasure: string;
    rating_vector_string: string | null;
    status: string;
    status_note?: string;
  }): Promise<{ ok: boolean }> => {
    const { cookie, ...body } = payload;
    const response = await fetch(`${BACKEND_BASE}/api/update-threat`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-ThreatCat-Cookie': cookie },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update threat in ThreatCat');
    }
    return response.json();
  },

  importDiagrams: async (
    diagrams: ParsedDiagram[],
    projectVersionId: string,
    cookie: string,
  ): Promise<{ imported: ImportedDiagramResult[] }> => {
    const response = await fetch(`${BACKEND_BASE}/api/threatcat/import-diagrams`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-ThreatCat-Cookie': cookie },
      body: JSON.stringify({
        project_version_id: projectVersionId,
        diagrams,
      }),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to import diagrams');
    }
    return response.json();
  },
};
